/** @jsx jsx */
import { jsx } from '@emotion/core'
import React, { Fragment } from 'react'
import { observer } from 'mobx-react'
import { BettingRecordTrack } from '@store/bettingHistoryStore'
import { BET_HIT_STATE } from 'adv-protocol/src/GameCommon'
import SharedSuccessImg from '@assets/images/icon/betting_history_icon/success-shared.png'
import SharedFailImg from '@assets/images/icon/betting_history_icon/fail-shared.png'
import SharedCancelImg from '@assets/images/icon/betting_history_icon/cancel-shared.png'
import SharedWatitingImg from '@assets/images/icon/betting_history_icon/waiting-shared.png'
import { Button } from '@styles/button.style'
import { BettingListFoldStyle, BettingListStyle } from '@views/Popup/Chat/index.style'
import BettingListBg from '@assets/images/bg/shared-betting-history-bg.png'
import * as _ from 'lodash'
import uuid from 'uuid'
import { getSportIconElementsAscendingOrderBySportIdAndRemoveDuplicated } from '@utils/jsxutils'
import useRootStore from '@store/useRootStore'
import { isMobile } from 'react-device-detect'
import BettingListFoldBg from '@assets/images/bg/shared-betting-history-single-bg.png'
import BettingListDoubleBg from '@assets/images/bg/shared-betting-history-double-bg.png'
import classNames from 'classnames'

interface SimpleBettingHistoryProps {
  bettingRecordTrack?: BettingRecordTrack
  onClickOpenBettingListInfo: (e: React.MouseEvent<HTMLElement>) => void
  className?: string
}

const SimpleBettingHistory: React.FC<SimpleBettingHistoryProps> = (props: SimpleBettingHistoryProps) => {
  const { matchupStore } = useRootStore()
  const { bettingRecordTrack, className = '', onClickOpenBettingListInfo } = props
  const { bet, folds } = bettingRecordTrack
  const { displayFolds, canceled } = bet

  const hitState = canceled === true ? BET_HIT_STATE.CANCEL : bet.hitState
  let hitStateClassName = 'state'
  let hitStateImg = <Fragment/>
  let showHitState: boolean
  switch (hitState) {
    case BET_HIT_STATE.SUCCESS:
      hitStateImg = <img src={SharedSuccessImg} alt="적중" className="hit-state-img"/>
      showHitState = true
      break
    case BET_HIT_STATE.FAIL:
      hitStateImg = <img src={SharedFailImg} alt="미적중" className="hit-state-img"/>
      showHitState = true
      break
    case BET_HIT_STATE.CANCEL:
      hitStateImg = <img src={SharedCancelImg} alt="적특" className="hit-state-img"/>
      showHitState = true
      break
    case BET_HIT_STATE.ONGOING:
      hitStateImg = <img src={SharedWatitingImg} alt="대기" className="hit-state-img state-ongoing"/>
      showHitState = true
      break
    default:
      hitStateClassName += ' adv-hidden'
      showHitState = false
      break
  }

  const sportIds = !_.isNil(displayFolds)
    ? [displayFolds[0].sportId]
    : _.map(folds, fold => fold.sportId)

  const sportIconElements = _.map(getSportIconElementsAscendingOrderBySportIdAndRemoveDuplicated(
    sportIds,
    matchupStore.feedSupplier
  ), element => (
    <span key={uuid.v4()}>
      {element}
    </span>
  ))

  const getDisplayFoldsHitState = (foldHitState: number) => {
    return canceled === true ? BET_HIT_STATE.CANCEL : foldHitState
  }

  if (!_.isEmpty(displayFolds)) {
    const backgroundImage = !_.isEmpty(displayFolds) ? BettingListFoldBg : BettingListDoubleBg
    const buttonClassName = classNames(className, {
      'is-fold': !_.isEmpty(displayFolds),
      'auto-uploaded': _.size(displayFolds) > 1,
    })

    return (
      <Button TP css={BettingListFoldStyle} onClick={onClickOpenBettingListInfo} className={buttonClassName}>
        <img src={backgroundImage} alt="베팅 내역 공유 이미지" className="betting-list-bg"/>
        <ul className="betting-list-info">
          <li className="betting-list-info--item top-info">
            <span className={hitStateClassName}>
              {hitStateImg}
            </span>
            <dl className="betting-list-info--item--result">
              <div className="betting-list-info--item--result--item">
                <dd className="betting-list-info--item--num">{bettingRecordTrack.bet.foldCount}</dd>
                <dt className="betting-list-info--item--txt">폴드</dt>
              </div>
              <div className="betting-list-info--item--result--item">
                <dd className="betting-list-info--item--num rate">
                  {Number(bettingRecordTrack.bet.dividendRate).toFixed(2)}
                </dd>
                <dt className="betting-list-info--item--txt rate">배당</dt>
              </div>
            </dl>
          </li>
          {_.map(displayFolds, fold => (
            <li className="betting-list-info--item bottom-info">
              <div className="select-match">
                <span className="outcome-team">{fold.selectionName}</span>
                <span className="outcome-rate" data-hit-state={getDisplayFoldsHitState(fold.hitState)}>{fold.outcomeRate}</span>
              </div>
              <div className="select-market">
                <span className="market-name">{fold.outcomeName}</span>
                <span className="market-rate">{fold.specialBetValue}</span>
              </div>
              { // 과거 공유된 경기내역에 팀명, 종목아이콘이 빈값으로 보이지 않도록 처리
                // 0010632: [V2공통] 경기내역 첨부_기준점,팀명,적중표시_추가
                // (https://task.zempublic.co.kr/view.php?id=10632)
                fold.homeTeamName && fold.awayTeamName && fold.sportId &&
                (
                  <div className="select-teams">
                    <div className="sport-icon">{sportIconElements}</div>
                    <span className="teams-name">(홈) {fold.homeTeamName} vs {fold.awayTeamName}</span>
                  </div>
                )}
            </li>
          ))}
        </ul>
      </Button>
    )
  }

  return (
    <Button TP css={BettingListStyle} onClick={onClickOpenBettingListInfo} className={className}>
      <img src={BettingListBg} alt="베팅 내역 공유 이미지" className="betting-list-bg"/>

      {showHitState && (
        <div className={hitStateClassName}>
          {hitStateImg}
        </div>
      )}

      <ul className="betting-list-info">
        <li className="betting-list-info--item">
          <span className="betting-list-info--item--num">{bettingRecordTrack.bet.foldCount}</span>
          <span className="betting-list-info--item--txt">폴드</span>
        </li>
        <li className="betting-list-info--item bottom">
          <div className={isMobile? 'sports-ico-wrap': 'sports-ico-wrap pc'}>
            {sportIconElements}
          </div>
          <div>
            <span className="betting-list-info--item--num rate">
              {Number(bettingRecordTrack.bet.dividendRate).toFixed(2)}
            </span>
            <span className="betting-list-info--item--txt">배당</span>
          </div>
        </li>
      </ul>
    </Button>
  )
}
export default observer(SimpleBettingHistory)
